import {QEPInput} from '@qep/shared/src/components/QEPInput/qep-input';
import {type TradableSymbol} from '@qep/types/src';
import React, {
  type ChangeEvent, useEffect, useRef, useState,
} from 'react';

type DefaultDisplayQuantitySetterProps = {
  tradableSymbol: TradableSymbol;
  handleChange: (value: number, sym: TradableSymbol) => void;
};

/** Renders an iceberg display-quantity control that preserves accepted input until blur resolves the edit. */
const DefaultDisplayQuantitySetter: React.FC<DefaultDisplayQuantitySetterProps> = ({
  tradableSymbol: symbol,
  handleChange,
}: DefaultDisplayQuantitySetterProps) => {
  const [displayQty, setDisplayQty] = useState<string>(symbol.icebergDisplayQty ? String(symbol.icebergDisplayQty) : '0');
  const hasUnsavedEdit = useRef(false);
  const hasInvalidEdit = useRef(false);

  /** Applies authoritative API updates when no edit is pending so the field does not retain a stale value. */
  useEffect(() => {
    if (!hasUnsavedEdit.current) {
      setDisplayQty(symbol.icebergDisplayQty ? String(symbol.icebergDisplayQty) : '0');
    }
  }, [symbol.icebergDisplayQty, symbol.rootSymbol, symbol.segmentMic]);

  /** Stores a valid non-negative edit and marks it dirty so authoritative updates cannot overwrite it. */
  const onDisplayQtyChanged = (event: ChangeEvent<HTMLInputElement>) => {
    const newQty = Number(event.target.value);
    if (Number.isNaN(newQty) || newQty < 0) {
      hasInvalidEdit.current = true;
      return;
    }

    hasInvalidEdit.current = false;
    hasUnsavedEdit.current = true;
    setDisplayQty(event.target.value);
  };

  /** Ends the edit lifecycle, restoring rejected edits or committing an accepted quantity while iceberg remains enabled. */
  const onDisplayQtyBlur = (event: ChangeEvent<HTMLInputElement>) => {
    const hasAcceptedEdit = hasUnsavedEdit.current;
    hasUnsavedEdit.current = false;
    if (hasInvalidEdit.current) {
      hasInvalidEdit.current = false;
      setDisplayQty(symbol.icebergDisplayQty ? String(symbol.icebergDisplayQty) : '0');
      return;
    }

    if (!hasAcceptedEdit) {
      return;
    }

    if (!symbol.isIceberg) {
      return;
    }

    const inputValue = event.target.value;
    const numberValue = Number(inputValue);
    if (inputValue && !Number.isNaN(numberValue) && numberValue > 0) {
      setDisplayQty(inputValue);
      handleChange(numberValue, symbol);
    } else {
      setDisplayQty('0');
      handleChange(0, symbol);
    }
  };

  return (
    <QEPInput
      className='qty-input display-qty-input'
      data-testid='display-qty-input'
      disabled={!symbol.isIceberg}
      onBlur={onDisplayQtyBlur}
      onChange={onDisplayQtyChanged}
      pattern='[0-9]*'
      type='number'
      value={displayQty}
    />
  );
};

export default DefaultDisplayQuantitySetter;
